/**
 * Chicane Fitment — Garage core + UI.
 *
 * Public API:
 *   ChicaneFitment.Garage.init()
 *   ChicaneFitment.Garage.addVehicle(vehicleId)
 *   ChicaneFitment.Garage.removeVehicle(id)
 *   ChicaneFitment.Garage.setActive(id)
 *   ChicaneFitment.Garage.getActive()
 *   ChicaneFitment.Garage.getAll()
 *   ChicaneFitment.Garage.clear()
 *   ChicaneFitment.Garage.UI.open()
 *   ChicaneFitment.Garage.UI.close()
 *
 * Events dispatched on document:
 *   chicane-garage:changed     (legacy name, kept for back-compat)
 *   chicane:garage:changed     (canonical name used by fitment-badge.js + fitment-results.js)
 *   chicane-garage:ready
 */

(function() {
  window.ChicaneFitment = window.ChicaneFitment || {};

  var GARAGE_KEY = 'chicane-garage-v1';
  var CART_ATTR_VEHICLE_NAME = 'Selected vehicle';
  var CART_ATTR_VEHICLE_ID = 'Vehicle ID';
  var CART_ATTR_VEHICLE_HANDLE = 'Vehicle handle';
  var FITMENT_PAGE_URL = '/pages/fitment?view=fitment';

  var garage = {
    vehicleIds: [],
    activeId: null
  };

  function saveToStorage() {
    try {
      localStorage.setItem(GARAGE_KEY, JSON.stringify(garage));
    } catch (e) {
      console.warn('[Garage] Failed to persist to localStorage', e);
    }
  }

  function loadFromStorage() {
    try {
      var raw = localStorage.getItem(GARAGE_KEY);
      if (!raw) return;
      var parsed = JSON.parse(raw);
      if (parsed && Array.isArray(parsed.vehicleIds)) {
        garage.vehicleIds = parsed.vehicleIds;
        garage.activeId = parsed.activeId || null;
      }
    } catch (e) {
      console.warn('[Garage] Failed to load from localStorage', e);
    }
  }

  function findVehicleById(id) {
    if (!window.ChicaneFitment.vehicles) return null;
    for (var i = 0; i < window.ChicaneFitment.vehicles.length; i++) {
      if (window.ChicaneFitment.vehicles[i].id === id) {
        return window.ChicaneFitment.vehicles[i];
      }
    }
    return null;
  }

  function syncCartAttributes() {
    var active = getActive();
    var attributes = {};

    if (active) {
      attributes[CART_ATTR_VEHICLE_NAME] = active.displayName;
      attributes[CART_ATTR_VEHICLE_ID] = active.id;
      attributes[CART_ATTR_VEHICLE_HANDLE] = active.handle;
    } else {
      attributes[CART_ATTR_VEHICLE_NAME] = '';
      attributes[CART_ATTR_VEHICLE_ID] = '';
      attributes[CART_ATTR_VEHICLE_HANDLE] = '';
    }

    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: attributes })
    }).then(function(r) {
      if (!r.ok) throw new Error('Cart update failed: HTTP ' + r.status);
      return r.json();
    }).catch(function(err) {
      console.warn('[Garage] Cart sync failed', err);
    });
  }

  function getAll() {
    return garage.vehicleIds
      .map(findVehicleById)
      .filter(function(v) { return v !== null; });
  }

  function getActive() {
    if (!garage.activeId) return null;
    return findVehicleById(garage.activeId);
  }

  function addVehicle(vehicleId) {
    if (!vehicleId) return;
    if (garage.vehicleIds.indexOf(vehicleId) === -1) {
      garage.vehicleIds.push(vehicleId);
    }
    garage.activeId = vehicleId;
    saveToStorage();
    syncCartAttributes();
    dispatchChanged();
  }

  function removeVehicle(vehicleId) {
    var idx = garage.vehicleIds.indexOf(vehicleId);
    if (idx === -1) return;
    garage.vehicleIds.splice(idx, 1);
    if (garage.activeId === vehicleId) {
      garage.activeId = garage.vehicleIds.length > 0 ? garage.vehicleIds[0] : null;
    }
    saveToStorage();
    syncCartAttributes();
    dispatchChanged();
  }

  function setActive(vehicleId) {
    if (garage.vehicleIds.indexOf(vehicleId) === -1) return;
    garage.activeId = vehicleId;
    saveToStorage();
    syncCartAttributes();
    dispatchChanged();
  }

  function clear() {
    garage.vehicleIds = [];
    garage.activeId = null;
    saveToStorage();
    syncCartAttributes();
    dispatchChanged();
  }

  function dispatchChanged() {
    var detail = {
      all: getAll(),
      active: getActive()
    };
    // Legacy event name (kept so nothing else breaks).
    document.dispatchEvent(new CustomEvent('chicane-garage:changed', { detail: detail }));
    // Canonical event name used by fitment-badge.js + fitment-results.js.
    document.dispatchEvent(new CustomEvent('chicane:garage:changed', { detail: detail }));
  }

  function init() {
    loadFromStorage();
    function finish() {
      var validIds = garage.vehicleIds.filter(function(id) {
        return findVehicleById(id) !== null;
      });
      if (validIds.length !== garage.vehicleIds.length) {
        garage.vehicleIds = validIds;
        if (garage.activeId && validIds.indexOf(garage.activeId) === -1) {
          garage.activeId = validIds.length > 0 ? validIds[0] : null;
        }
        saveToStorage();
      }
      document.dispatchEvent(new CustomEvent('chicane-garage:ready', {
        detail: {
          all: getAll(),
          active: getActive()
        }
      }));
      console.log('[Garage] Initialized with', garage.vehicleIds.length, 'vehicle(s)');
    }

    if (window.ChicaneFitment.vehicles && window.ChicaneFitment.vehicles.length > 0) {
      finish();
    } else {
      document.addEventListener('chicane-fitment:ready', finish, { once: true });
    }
  }

  var Catalog = {
    allMakes: function() {
      if (!window.ChicaneFitment.vehicles) return [];
      var seen = {};
      var makes = [];
      window.ChicaneFitment.vehicles.forEach(function(v) {
        if (v.make && !seen[v.make]) {
          seen[v.make] = true;
          makes.push(v.make);
        }
      });
      return makes.sort();
    },

    modelsForMake: function(make) {
      if (!window.ChicaneFitment.vehicles) return [];
      var seen = {};
      var models = [];
      window.ChicaneFitment.vehicles.forEach(function(v) {
        if (v.make === make && v.model && !seen[v.model]) {
          seen[v.model] = true;
          models.push(v.model);
        }
      });
      return models.sort();
    },

    submodelsForMakeModel: function(make, model) {
      if (!window.ChicaneFitment.vehicles) return [];
      var seen = {};
      var submodels = [];
      window.ChicaneFitment.vehicles.forEach(function(v) {
        if (v.make === make && v.model === model && v.submodel && !seen[v.submodel]) {
          seen[v.submodel] = true;
          submodels.push(v.submodel);
        }
      });
      return submodels.sort();
    },

    yearsForMakeModelSubmodel: function(make, model, submodel) {
      if (!window.ChicaneFitment.vehicles) return [];
      var years = [];
      window.ChicaneFitment.vehicles.forEach(function(v) {
        if (v.make === make && v.model === model && v.submodel === submodel && v.year) {
          years.push(v.year);
        }
      });
      return years.sort(function(a, b) { return b - a; });
    },

    findVehicle: function(year, make, model, submodel) {
      if (!window.ChicaneFitment.vehicles) return null;
      for (var i = 0; i < window.ChicaneFitment.vehicles.length; i++) {
        var v = window.ChicaneFitment.vehicles[i];
        if (v.year === year && v.make === make && v.model === model && v.submodel === submodel) {
          return v;
        }
      }
      return null;
    },

    searchByText: function(query) {
      if (!query || !window.ChicaneFitment.vehicles) return [];
      var q = query.toLowerCase().trim();
      if (q.length < 2) return [];
      return window.ChicaneFitment.vehicles.filter(function(v) {
        return v.displayName.toLowerCase().indexOf(q) !== -1
          || v.submodel.toLowerCase().indexOf(q) !== -1;
      }).slice(0, 20);
    }
  };

  // ============================================================
  // UI layer — modal
  // ============================================================
  var UI = {
    _initialized: false,
    _state: { make: '', model: '', submodel: '', year: '' },

    init: function() {
      if (this._initialized) return;
      var modal = document.getElementById('cc-garage-modal');
      if (!modal) return;

      modal.querySelectorAll('[data-cc-garage-close]').forEach(function(el) {
        el.addEventListener('click', function() { UI.close(); });
      });
      document.addEventListener('keydown', function(e) {
        if (e.key === 'Escape' && modal.classList.contains('is-open')) UI.close();
      });

      this._wireCascade();

      document.getElementById('cc-garage-add-btn').addEventListener('click', function() {
        UI._handleAdd();
      });

      document.addEventListener('chicane-garage:changed', function() { UI._renderList(); });
      document.addEventListener('chicane-garage:ready', function() { UI._renderList(); });

      this._initialized = true;
    },

    open: function() {
      this.init();
      var modal = document.getElementById('cc-garage-modal');
      if (!modal) return;
      this._renderList();
      this._populateMakes();
      modal.classList.add('is-open');
      modal.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
    },

    close: function() {
      var modal = document.getElementById('cc-garage-modal');
      if (!modal) return;
      modal.classList.remove('is-open');
      modal.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
    },

    _renderList: function() {
      var listEl = document.getElementById('cc-garage-list');
      var emptyEl = document.getElementById('cc-garage-empty');
      if (!listEl || !emptyEl) return;

      var all = getAll();
      var active = getActive();
      listEl.innerHTML = '';

      if (all.length === 0) {
        emptyEl.style.display = 'block';
        return;
      }
      emptyEl.style.display = 'none';

      all.forEach(function(v) {
        var isActive = active && active.id === v.id;
        var li = document.createElement('li');
        li.className = 'cc-garage-list__item' + (isActive ? ' is-active' : '');
        li.innerHTML = [
          '<svg class="cc-garage-list__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">',
            '<path d="M3 12l2-5a2 2 0 0 1 1.8-1.2h10.4A2 2 0 0 1 19 7l2 5"/>',
            '<rect x="3" y="12" width="18" height="6" rx="1"/>',
            '<circle cx="7" cy="18" r="1.5"/>',
            '<circle cx="17" cy="18" r="1.5"/>',
          '</svg>',
          '<span class="cc-garage-list__name">', v.displayName, '</span>',
          isActive
            ? '<a class="cc-garage-list__shop" href="' + FITMENT_PAGE_URL + '" data-cc-garage-shop>'
                + 'Shop parts'
                + '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">'
                  + '<path d="M5 12h14"/><path d="m12 5 7 7-7 7"/>'
                + '</svg>'
              + '</a>'
            : '',
          '<button class="cc-garage-list__remove" aria-label="Remove vehicle">',
            '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">',
              '<polyline points="3 6 5 6 21 6"/>',
              '<path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>',
            '</svg>',
          '</button>'
        ].join('');

        li.addEventListener('click', function(e) {
          if (e.target.closest('.cc-garage-list__remove')) return;
          if (e.target.closest('[data-cc-garage-shop]')) return;
          setActive(v.id);
        });

        var shopLink = li.querySelector('[data-cc-garage-shop]');
        if (shopLink) {
          shopLink.addEventListener('click', function(e) {
            // Let the link navigate normally, but close the modal first.
            UI.close();
          });
        }

        li.querySelector('.cc-garage-list__remove').addEventListener('click', function(e) {
          e.stopPropagation();
          removeVehicle(v.id);
        });

        listEl.appendChild(li);
      });
    },

    _populateMakes: function() {
      var select = document.getElementById('cc-garage-make');
      if (!select) return;
      var makes = Catalog.allMakes();
      select.innerHTML = '<option value="">Select make</option>' +
        makes.map(function(m) { return '<option value="' + m + '">' + m + '</option>'; }).join('');
      this._state = { make: '', model: '', submodel: '', year: '' };
      this._resetField('model');
      this._resetField('submodel');
      this._resetField('year');
      document.getElementById('cc-garage-add-btn').disabled = true;
    },

    _wireCascade: function() {
      var makeEl = document.getElementById('cc-garage-make');
      var modelEl = document.getElementById('cc-garage-model');
      var submodelEl = document.getElementById('cc-garage-submodel');
      var yearEl = document.getElementById('cc-garage-year');

      makeEl.addEventListener('change', function(e) {
        UI._state.make = e.target.value;
        UI._state.model = '';
        UI._state.submodel = '';
        UI._state.year = '';
        if (!UI._state.make) {
          UI._resetField('model'); UI._resetField('submodel'); UI._resetField('year');
          UI._updateSubmit(); return;
        }
        var models = Catalog.modelsForMake(UI._state.make);
        modelEl.innerHTML = '<option value="">Select model</option>' +
          models.map(function(m) { return '<option value="' + m + '">' + m + '</option>'; }).join('');
        modelEl.disabled = false;
        UI._resetField('submodel');
        UI._resetField('year');
        UI._updateSubmit();
      });

      modelEl.addEventListener('change', function(e) {
        UI._state.model = e.target.value;
        UI._state.submodel = '';
        UI._state.year = '';
        if (!UI._state.model) {
          UI._resetField('submodel'); UI._resetField('year');
          UI._updateSubmit(); return;
        }
        var submodels = Catalog.submodelsForMakeModel(UI._state.make, UI._state.model);
        submodelEl.innerHTML = '<option value="">Select chassis</option>' +
          submodels.map(function(s) { return '<option value="' + s + '">' + s + '</option>'; }).join('');
        submodelEl.disabled = false;
        UI._resetField('year');
        UI._updateSubmit();
      });

      submodelEl.addEventListener('change', function(e) {
        UI._state.submodel = e.target.value;
        UI._state.year = '';
        if (!UI._state.submodel) {
          UI._resetField('year');
          UI._updateSubmit(); return;
        }
        var years = Catalog.yearsForMakeModelSubmodel(UI._state.make, UI._state.model, UI._state.submodel);
        yearEl.innerHTML = '<option value="">Select year</option>' +
          years.map(function(y) { return '<option value="' + y + '">' + y + '</option>'; }).join('');
        yearEl.disabled = false;
        UI._updateSubmit();
      });

      yearEl.addEventListener('change', function(e) {
        UI._state.year = parseInt(e.target.value, 10) || '';
        UI._updateSubmit();
      });
    },

    _resetField: function(name) {
      var el = document.getElementById('cc-garage-' + name);
      if (!el) return;
      var label = name === 'submodel' ? 'chassis' : name;
      el.innerHTML = '<option value="">Select ' + label + '</option>';
      el.disabled = true;
    },

    _updateSubmit: function() {
      var btn = document.getElementById('cc-garage-add-btn');
      var s = this._state;
      btn.disabled = !(s.make && s.model && s.submodel && s.year);
    },

    _handleAdd: function() {
      var s = this._state;
      var v = Catalog.findVehicle(s.year, s.make, s.model, s.submodel);
      if (!v) {
        alert('Could not find that vehicle in the catalog.');
        return;
      }
      addVehicle(v.id);
      this._populateMakes();
    }
  };

  window.ChicaneFitment.Garage = {
    init: init,
    addVehicle: addVehicle,
    removeVehicle: removeVehicle,
    setActive: setActive,
    getActive: getActive,
    getAll: getAll,
    clear: clear,
    Catalog: Catalog,
    UI: UI
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();